<template>
  <Modal
    v-model:visible="visible"
    class="ship-order-split-modal"
    :header="t('shipOrders.shipOrderSplit.split')"
  >
    <Card class="ship-order-split-card">
      <template #content>
        <!-- header -->
        <div class="top-row flex justify-content-between align-items-center">
          <div class="top-row-info flex flex-gap-1">
            <h3 class="ship-order-split-card-header" tabindex="-1">
              {{
                t('shipOrders.shipOrderNumParam', {
                  param: `${shipOrder.orderId}-${shipOrder.id}`,
                })
              }}
            </h3>
          </div>
        </div>

        <!-- location -->
        <div
          class="additional-row flex flex-gap-1 justify-content-between align-items-center text-gray-darkest"
        >
          <div
            class="ship-order-split-card-shipping-point-info ship-order-split-card-shipping-point-info-origin"
          >
            <NamedAddress
              :name="shipOrder?.origin?.location?.locationName"
              :address="shipOrder?.origin?.location?.toAddress()"
              suppress-address-lines
              supress-postal-code
            />
            <div>
              {{
                renderDateTime(
                  shipOrder?.origin?.requestedAppointmentEarliest,
                  {
                    timeFormat: 'NONE',
                  },
                )
              }}
            </div>
          </div>
          <FontAwesomeIcon
            aria-hidden="true"
            class="text-gray-darker"
            :icon="['fal', 'long-arrow-right']"
          />
          <div
            class="ship-order-split-card-shipping-point-info ship-order-split-card-shipping-point-info-destination"
          >
            <NamedAddress
              :name="shipOrder?.destination?.location?.locationName"
              :address="shipOrder?.destination?.location?.toAddress()"
              suppress-address-lines
              supress-postal-code
            />
            <div>
              {{
                renderDateTime(
                  shipOrder?.destination?.requestedAppointmentEarliest,
                  { timeFormat: 'NONE' },
                )
              }}
            </div>
          </div>
        </div>

        <!-- line items -->
        <DataTable
          :value="shipOrderSplitBuilder.lineItemSplits"
          class="ship-order-split-line-items"
        >
          <Column :header="t('shipOrders.shipOrderSplit.qtyOriginal')">
            <template #body="{ data: lineItemSplit }">
              {{
                lineItemSplit.shipOrderLineItem.quantity +
                  ' ' +
                  lineItemSplit.shipOrderLineItem.handlingUnit?.name || ''
              }}
            </template>
          </Column>
          <Column :header="t('shipOrders.shipOrderSplit.qtyToKeep')">
            <template #body="{ data: lineItemSplit }">
              <InputNumber
                v-model="lineItemSplit.qtyToKeep"
                :min="0"
                :max="lineItemSplit.shipOrderLineItem.quantity"
                show-buttons
              />
            </template>
          </Column>
          <Column :header="t('shipOrders.shipOrderSplit.qtyOnNew')">
            <template #body="{ data: lineItemSplit }">
              <InputNumber
                v-model="lineItemSplit.qtyOnNew"
                :min="0"
                :max="lineItemSplit.shipOrderLineItem.quantity"
                show-buttons
              />
            </template>
          </Column>
          <Column
            field="shipOrderLineItem.description"
            :header="t('lineItems.lineItems')"
            header-style="min-width: 200px;"
          />
        </DataTable>
      </template>
    </Card>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="visible = false"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :label="t('common.save')"
        @click="onSave"
      />
    </template>
  </Modal>
  <BlockUI :blocked="saving" full-screen />
</template>

<script lang="ts" setup>
import axios from 'axios';
import { ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { renderDateTime } from '@rygen/rygen-ui/i18n';

import api from '@/api';
import { ShipOrder, ShipOrderLineItem, ShipOrderSplitCommand } from '@/model';

import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import BlockUI from 'primevue/blockui';
import Card from 'primevue/card';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';

type ValidShipOrderLineItem = ShipOrderLineItem & {
  id: number;
  quantity: number;
};
type ValidShipOrder = ShipOrder & {
  id: number;
  lineItems: ValidShipOrderLineItem[];
};

const isValidShipOrderLineItem = (
  shipOrderLineItem: ShipOrderLineItem,
): shipOrderLineItem is ValidShipOrderLineItem =>
  !!shipOrderLineItem.id && !!shipOrderLineItem.quantity;

const isValidShipOrder = (shipOrder: ShipOrder): shipOrder is ValidShipOrder =>
  !!shipOrder.id && shipOrder.lineItems.every(isValidShipOrderLineItem);

class ShipOrderLineItemSplitBuilder {
  readonly shipOrderLineItem: ValidShipOrderLineItem;
  private _qtyToKeep: number = 0;
  private _qtyOnNew: number = 0;

  constructor(shipOrderLineItem: ShipOrderLineItem) {
    if (!isValidShipOrderLineItem(shipOrderLineItem)) {
      throw new Error('Invalid ShipOrderLineItem');
    }

    this.shipOrderLineItem = shipOrderLineItem;
    this.qtyToKeep = shipOrderLineItem.quantity;
  }

  get qtyToKeep(): number {
    return this._qtyToKeep;
  }

  set qtyToKeep(qty: number) {
    if (qty < 0) {
      qty = 0;
    } else if (qty > this.shipOrderLineItem.quantity) {
      qty = this.shipOrderLineItem.quantity;
    }

    this._qtyToKeep = qty;
    this._qtyOnNew = this.shipOrderLineItem.quantity - qty;
  }

  get qtyOnNew(): number {
    return this._qtyOnNew;
  }

  set qtyOnNew(qty: number) {
    if (qty < 0) {
      qty = 0;
    } else if (qty > this.shipOrderLineItem.quantity) {
      qty = this.shipOrderLineItem.quantity;
    }

    this._qtyOnNew = qty;
    this._qtyToKeep = this.shipOrderLineItem.quantity - qty;
  }
}

class ShipOrderSplitBuilder {
  readonly shipOrder: ValidShipOrder;
  readonly lineItemSplits: ShipOrderLineItemSplitBuilder[];

  constructor(shipOrder: ShipOrder) {
    if (!isValidShipOrder(shipOrder)) {
      throw new Error('Invalid ShipOrder');
    }

    this.shipOrder = shipOrder;
    this.lineItemSplits = shipOrder.lineItems.map(
      (li) => new ShipOrderLineItemSplitBuilder(li),
    );
  }

  public toCommand(): ShipOrderSplitCommand {
    return {
      shipOrderId: this.shipOrder.id,
      splitLineItems: this.lineItemSplits.map((li) => ({
        shipOrderLineItemId: li.shipOrderLineItem.id,
        qtyToKeep: li.qtyToKeep,
        qtyOnNew: li.qtyOnNew,
      })),
    };
  }
}

const visible = defineModel<boolean>('visible', {
  type: Boolean,
  required: true,
});

const props = defineProps<{
  shipOrder: ShipOrder;
}>();

const { shipOrder } = toRefs(props);

const emit = defineEmits<{
  (event: 'refresh-candidates'): void;
}>();

const { t } = useI18n();
const toast = useToast();

const saving = ref(false);
const shipOrderSplitBuilder = ref(new ShipOrderSplitBuilder(shipOrder.value));

watch(
  () => shipOrder.value,
  (newShipOrder) => {
    shipOrderSplitBuilder.value = new ShipOrderSplitBuilder(newShipOrder);
  },
  { deep: true },
);

const onSave = async (): Promise<void> => {
  saving.value = true;
  try {
    const shipOrderSplitCommand = shipOrderSplitBuilder.value.toCommand();
    await api.shipOrder.split(shipOrderSplitCommand);
    emit('refresh-candidates');
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error splitting ship order', e);
    toast.addError({
      detail: t('shipOrders.shipOrderSplit.errors.splitting'),
      error: e,
    });
  } finally {
    saving.value = false;
  }
};
</script>

<style>
.ship-order-split-modal.p-dialog.rygen-modal {
  width: 40vw;
  max-width: 750px;

  .p-dialog-content {
    padding-top: 0;
  }
}

.ship-order-split-card.p-card {
  .p-card-body {
    padding: 0;

    .top-row {
      background-color: var(--p-gray-200);

      .top-row-info {
        padding: var(--sp-3);
        margin-right: auto;

        .ship-order-split-card-header {
          color: var(--p-gray-600);
          font-size: 14px;
          font-weight: 700;
        }
      }
    }

    .additional-row {
      padding: var(--sp-3);
      font-size: 12px;
      font-weight: 400;

      & + .additional-row {
        padding-top: 0;
      }

      .pi.pi-arrow-right {
        transform: scaleX(150%);
      }
    }
  }
}

.ship-order-split-line-items.p-component.p-datatable {
  .p-datatable-table .p-datatable-thead {
    border-top-width: 0;
    border-left-width: 0;
    border-right-width: 0;

    & > tr > th {
      background: unset;
      font-size: 10px;
      color: var(--p-gray-600);
      font-weight: 400;
    }
  }

  .p-inputnumber {
    max-width: 100px;
  }
}
</style>
